/* ProductSpecifications - Displays only the factual plant and variant specifications. */

import { motion } from 'framer-motion';
import {
  Sun,
  TrendingUp,
  Scissors,
  Leaf,
  Mountain,
  Snowflake,
  Sprout,
} from 'lucide-react';

interface Product {
  standplaats: string[];
  groeisnelheid: string;
  snoeien: string;
  groei_per_jaar: string;
  wintergroen: boolean;
  winterhard: boolean;
  maximale_hoogte: string;
  geslacht: string;
}

interface ProductVariant {
  size: string;
  type: string;
}

interface ProductSpecificationsProps {
  product: Product;
  variant: ProductVariant;
}

export default function ProductSpecifications({ product, variant }: ProductSpecificationsProps) {
  const specifications = [
    { icon: Sprout, title: 'Maat', content: variant.size, show: true },
    { icon: Mountain, title: 'Type', content: variant.type, show: true },
    { icon: Sun, title: 'Standplaats', content: product.standplaats?.join(', ') || 'Informatie niet beschikbaar', show: true },
    { icon: TrendingUp, title: 'Groeisnelheid', content: product.groeisnelheid || 'Informatie niet beschikbaar', show: product.groeisnelheid },
    { icon: Scissors, title: 'Snoeien', content: product.snoeien || 'Informatie niet beschikbaar', show: product.snoeien },
    { icon: TrendingUp, title: 'Groei per jaar', content: product.groei_per_jaar || 'Informatie niet beschikbaar', show: product.groei_per_jaar },
    { icon: Mountain, title: 'Maximale hoogte', content: product.maximale_hoogte || 'Informatie niet beschikbaar', show: product.maximale_hoogte },
    { icon: Snowflake, title: 'Winterhard', content: product.winterhard ? 'Ja' : 'Nee', show: true },
    { icon: Leaf, title: 'Wintergroen', content: product.wintergroen ? 'Ja' : 'Nee', show: true },
    { icon: Sprout, title: 'Geslacht', content: product.geslacht || 'Informatie niet beschikbaar', show: product.geslacht }
  ].filter(spec => spec.show);

  return (
    <section className="mb-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="mb-8">
          <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Alles op een rij</p>
          <h2 className="text-3xl tracking-[-0.035em] text-[#1e2b22] md:text-4xl">Specificaties</h2>
          <p className="mt-3 text-base leading-7 text-[#5a6552]">Alle eigenschappen van deze plant op een rij</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {specifications.map((spec, index) => (
            <motion.div key={spec.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }} whileHover={{ y: -4 }}>
              <div className="h-full border-l-2 border-[#e7e1d6] pl-5 transition-[border-color] duration-300 hover:border-[#1e2b22]">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-[#e7e1d6] text-[#1e2b22]">
                  <spec.icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="mb-1.5 text-sm font-semibold uppercase tracking-wide text-[#1e2b22]">{spec.title}</h3>
                <p className="text-base leading-7 text-[#5a6552]">{spec.content}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
